import { Claim } from 'interfaces';
import { MRT_ColumnDef } from 'mantine-react-table';
import { PersistedVisibleColumns } from 'store/SettingsStore';
import { AllKeys } from './ModalComponents';

export const CLAIM_ACTION_COLUMN_ID = 'mrt-history';

export const getDefaultColumnVisibility = <T extends Claim>(
  columns: MRT_ColumnDef<T>[],
  defaultVisibleColumns: AllKeys[],
  persistedVisibleColumns: PersistedVisibleColumns | null = null,
  isProvider?: boolean,
  isAllSites?: boolean
) => {
  const allKeys = columns
    .map((column) => column.accessorKey as string | undefined)
    .filter(Boolean) as string[];

  const visibility: PersistedVisibleColumns = Object.fromEntries(
    allKeys.map((key) => [key, false])
  );

  const hasPersisted =
    !!persistedVisibleColumns &&
    Object.keys(persistedVisibleColumns).length > 0;

  if (hasPersisted) {
    for (const key of Object.keys(persistedVisibleColumns)) {
      if (key in visibility) {
        visibility[key] = persistedVisibleColumns[key];
      }
    }
  } else {
    for (const key of defaultVisibleColumns) {
      if (key in visibility) {
        visibility[key] = true;
      }
    }
  }

  if (isProvider && 'siteID' in visibility) {
    visibility.siteID = isAllSites;
  }
  if (isProvider && 'claimComments' in visibility) {
    visibility.claimComments = false;
  }

  return visibility;
};

export const initialVisibleColumns: AllKeys[] = [
  'serviceStart',
  'createdAt',
  'patientID',
  'healthcard.number',
  'providerID',
  'tariffs',
  'status',
  'icd9',
  'debits.requestedAmount',
  'debits.paidAmount',
  'payorID',
  'debits.eobs',
  'claimNumber',
  'intakeItemID',
  'submissions.submittedAt',
];

export const sortableColumns: AllKeys[] = [
  'version',
  'serviceStart',
  'createdAt',
  'updatedAt',
  'patientID',
  'providerID',
  'debits.paidAmount',
  'debits.eobs',
  'submissions.submittedAt',
];

export const filterableColumns: AllKeys[] = [
  'serviceStart',
  'createdAt',
  'patientID',
  'providerID',
  'status',
  'payorID',
  'healthcard.number',
  'remittances',
  'debits.eobs',
  'debits.requestedAmount',
  'debits.paidAmount',
  'claimNumber',
  'tariffs',
  'tariffs.icd9',
  'icd9',
  'intakeItemID',
  'submissions.submittedAt',
  'createdByUser',
  'claimComments',
];

export const providerInitialVisibleColumns: AllKeys[] = [
  'serviceStart',
  'tariffs.lastSubmittedAt',
  'debits.createdAt',
  'providerComments',
  'tariffs',
  'status',
  'debits.requestedAmount',
  'debits.paidAmount',
  'outstandingAmount',
  'debits.eobs',
  'tariffs.latestComment',
];

export const getProviderColumnOrder = (columnKeys: string[]): string[] => [
  ...providerInitialVisibleColumns.filter((key) => columnKeys.includes(key)),
  ...columnKeys.filter(
    (key) => !providerInitialVisibleColumns.some((visible) => visible === key)
  ),
];

// Columns to hide by default for provider users
// also will be hidden in the column toggler
export const providerHiddenColumns: AllKeys[] = [
  'version',
  'claimComments',
  'chiroService',
  'locationOfService',
  'patientCount',
  'serviceLocationIndicator',
  'incorporated',
  'claimType',
  'claimMode',
  'formType',
  'corporationIndicator',
  'specialCircumstances',
  'hospitalName',
  'facilityNumber',
  'provider',
  'siteID',
  'site.siteNumber',
  'patient.organizationID',
  'provider.license',
  'provider.specialtyCode',
  'tariffs.type',
  'submissions.submittedAt',
  'remittances',
  'resubmit',
  'healthcard.province',
  'healthcard.registration',
  'tariffs.rate',
  'tariffs.units',
  'tariffs.split',
  'tariffs.incision',
  'tariffs.code',
  'provider.licenses',
  'provider.firstName',
  'provider.lastName',
  'provider.contactNote',
  'patient.phone',
  'patient.postalCode',
  'patient.sex',
  'patient.dateOfBirth',
  'patient.country',
  'referringProviderObject',
  'clerkComments',
  'providerNotes',
  'tariffs.icd9',
  'interpretingRadiologist',
  'virtualPhysicianRegion',
  'optometristReason',
  'thirdPartyLiability',
  'hospitalCode',
  'chiroService',
  'debits',
  'providerID',
  'payorID',
  'credit.amount',
  'patient.firstName',
  'patient.lastName',
  'patient.address',
  'patient.city',
  'patient.province',
  'messageID',
  'intakeItemID',
  //@ts-expect-error -- keys exist --
  'debits.eob.description',
];

export const getDefaultVisibleColumns = (isProvider: boolean): AllKeys[] =>
  isProvider
    ? providerInitialVisibleColumns
    : initialVisibleColumns.filter(
        (col) => !(isProvider && col === 'providerID')
      );

// Helper: apply sorting, filtering and visibility metadata to columns
export const enhanceColumns = <T extends Claim>(
  columns: MRT_ColumnDef<T>[],
  isProvider: boolean,
  isAllSites = false
): MRT_ColumnDef<T>[] => {
  columns.forEach((column) => {
    const key = column.accessorKey as AllKeys;
    column.enableSorting = sortableColumns.includes(key);
    column.enableColumnFilter = filterableColumns.includes(key);
    const isSiteColumnForAllSites =
      key === 'siteID' && isProvider && isAllSites;
    column.visibleInShowHideMenu = isProvider
      ? isSiteColumnForAllSites || !providerHiddenColumns.includes(key)
      : true;
  });
  return columns;
};
